import React from 'react';
import { useForm, SubmitHandler, FieldValues } from 'react-hook-form';
import {
  useApiClient,
  useFetch,
} from '@marageti/z4-lib';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { TeamMember } from '@marageti/z4-sdk/lib/people';
import { User } from '@marageti/z4-sdk/lib/auth';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import EditUserFormView from './edit-user-form-view';
import { ContainerProps } from './types';

const EditUserFormContainer = ({
  open,
  onClose,
}: ContainerProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { userId } = useParams<{ userId: string }>();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
  });

  const apiClient = useApiClient();

  const [submissionError, setSubmissionError] = React.useState<ApiError>();

  const { data: teamMember, mutate: mutateTM } = useFetch<TeamMember>(apiClient.peopleClient.getTeamMember, [userId], true);
  const { data: user, mutate: mutateUser } = useFetch<User>(apiClient.authClient.getUser, [teamMember?.userInfo[0].id], true);

  const defaultSelectedRoles = teamMember?.userInfo[0]?.roles || [];

  const handleCancel = () => {
    reset();
    onClose();
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    if (teamMember && user) {
      const updatedTeamMember = {
        ...teamMember,
        givenName: data.givenName,
        familyName: data.familyName,
        phone: {
          number: data.phone?.number,
          prefix: data.phone?.prefix,
        },
        timeZone: data.timeZone,
      };

      const updatedUser = {
        ...user,
        roles: data.roles,
      };

      try {
        await apiClient.peopleClient.putTeamMember(teamMember.id, updatedTeamMember);
        await apiClient.authClient.putUser(user.id, updatedUser);
        mutateUser();
        mutateTM();
        enqueueSnackbar('User updated.', { variant: 'success' });
        onClose();
      } catch (error) {
        setSubmissionError(error as ApiError);
        enqueueSnackbar('An error occurred during the update.', { variant: 'default' });
      }
    }
  };

  return (
    <EditUserFormView
      control={control}
      defaultSelectedRoles={defaultSelectedRoles}
      errors={errors}
      handleSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      isValid={isValid}
      onCancel={handleCancel}
      onSubmit={onSubmit}
      open={open}
      register={register}
      submissionError={submissionError}
      teamMember={teamMember}
    />
  );
};

export default EditUserFormContainer;
